import Download from "@mui/icons-material/Download";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FilterList from "@mui/icons-material/FilterList";
import {
  Badge,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CommandButton } from "components/CommandButton";
import { ExportDataButton } from "components/ExportDataButton";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useTenant } from "hooks/useTenant";
import { MouseEvent, ReactNode, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";

import { useWasteFacility } from "../../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteCodeFilterPopover, WasteCodeFilters } from "./FilterPopover";
import { WasteCodeGrid } from "./Grid";
import { WasteCodeCompareDialog } from "./WasteCodeCompareDialog";

export interface WasteCodesData {
  federalWasteCodes: string[];
  stateWasteCodes: string[];
}

export interface WasteCodesContentProps {
  /** The waste codes data to display */
  data?: WasteCodesData;
  /** Error state */
  error?: Error | null;
  /** Optional custom actions to display in the header */
  customActions?: ReactNode;
  /** Items per page for pagination */
  itemsPerPage?: number;
  /** Custom height for the grid container */
  height?: string;
  /** Custom titles for the grid columns */
  titles?: {
    federal: string;
    state: string;
  };
}

export const WasteCodesContent = ({
  data,
  error = null,
  customActions,
  itemsPerPage = 10,
  height,
  titles = {
    federal: "Federal Hazardous Waste Codes",
    state: "State Hazardous Waste Codes",
  },
}: WasteCodesContentProps) => {
  const theme = useTheme();
  const { user, isStaff } = useCurrentUser();
  const { tenantId } = useTenant();
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: facilityData } = useWasteFacility(facilityId);
  const [searchTerm, setSearchTerm] = useState("");
  const [federalCompareOpen, setFederalCompareOpen] = useState(false);
  const [stateCompareOpen, setStateCompareOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();

  // Filter state with localStorage persistence
  const [filters, setFilters] = useLocalStorageState<WasteCodeFilters>(
    `waste-codes-filters|${user?.email}`,
    {
      defaultValue: {
        federalSearch: "",
        stateSearch: "",
      },
    }
  );

  const exportButtonId = "export-button";
  const menuId = "export-menu";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setFilterAnchor(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterAnchor(null);
  }, []);

  const handleApplyFilters = useCallback(
    (newFilters: WasteCodeFilters) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const handleClearFilters = useCallback(() => {
    setFilters({
      federalSearch: "",
      stateSearch: "",
    });
  }, [setFilters]);

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  // Calculate active filter count for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.federalSearch) count++;
    if (filters.stateSearch) count++;
    return count;
  }, [filters]);

  const getExportFileName = useCallback(
    (type: "state" | "federal") =>
      facilityData.facility?.name ? `${facilityData.facility.name}-${type}-waste-codes` : undefined,
    [facilityData.facility?.name]
  );

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Error loading waste codes</Typography>
      </Box>
    );
  }

  const containerStyle = height ? { height } : { minHeight: theme.spacing(66) };

  return (
    <Stack spacing={3} sx={containerStyle}>
      <Stack spacing={3} direction="row" alignItems="center">
        <TextField
          fullWidth
          placeholder="Search"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ maxWidth: theme.spacing(170) }}
        />
        <Badge badgeContent={activeFilterCount} color="primary">
          <CommandButton onClick={handleFilterButtonClick}>
            <FilterList sx={{ mr: 1 }} fontSize="small" />
            Filters
          </CommandButton>
        </Badge>
        <Button
          id={exportButtonId}
          aria-controls={open ? menuId : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          variant="text"
          onClick={handleExportClick}
          startIcon={<Download />}
          endIcon={<ExpandMore />}
          disabled={
            (!data?.federalWasteCodes || data.federalWasteCodes.length === 0) &&
            (!data?.stateWasteCodes || data.stateWasteCodes.length === 0)
          }
          sx={{ px: 2 }}
        />
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={open}
          onClose={handleExportClose}
          MenuListProps={{
            "aria-labelledby": exportButtonId,
          }}
        >
          <MenuList dense>
            <MenuItem key="export-federal-waste-codes">
              <ExportDataButton
                key="export-federal-waste-codes-button"
                exportType="wasteCodes"
                disabled={!facilityId}
                facilityId={facilityId}
                tenantId={tenantId ?? ""}
                title="Federal Waste Codes"
                fileName={getExportFileName("federal")}
                filter={{
                  search: searchTerm + " " + filters.federalSearch,
                  startDate: deferredFilters.startDate,
                  endDate: deferredFilters.endDate,
                  view: deferredFilters.view,
                  tags: deferredFilters.tags,
                  type: "federal",
                }}
              />
            </MenuItem>
            <MenuItem key="export-state-waste-codes">
              <ExportDataButton
                key="export-state-waste-codes-button"
                exportType="wasteCodes"
                disabled={!facilityId}
                facilityId={facilityId}
                tenantId={tenantId ?? ""}
                title="State Waste Codes"
                fileName={getExportFileName("state")}
                filter={{
                  search: searchTerm + " " + filters.stateSearch,
                  startDate: deferredFilters.startDate,
                  endDate: deferredFilters.endDate,
                  view: deferredFilters.view,
                  tags: deferredFilters.tags,
                  type: "state",
                }}
              />
            </MenuItem>
          </MenuList>
        </Menu>
        {customActions}
      </Stack>

      <WasteCodeFilterPopover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        filters={filters}
        onClose={handleFilterClose}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        activeFilterCount={activeFilterCount}
      />

      <Grid container spacing={3}>
        <WasteCodeGrid
          codes={data?.federalWasteCodes || []}
          title={titles.federal}
          searchTerm={searchTerm}
          specificSearchTerm={filters.federalSearch}
          itemsPerPage={itemsPerPage}
          keyPrefix="federal"
          emptyStateMessage={{
            withSearch: "No federal waste codes match your search",
            withoutSearch: "No federal waste codes found",
          }}
          fullWidth={!data?.stateWasteCodes || data.stateWasteCodes.length === 0}
          headerAction={
            isStaff && (data?.federalWasteCodes?.length ?? 0) > 0 ? (
              <Button
                size="small"
                variant="contained"
                sx={{
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.warning.light,
                }}
                onClick={() => setFederalCompareOpen(true)}
              >
                Compare
              </Button>
            ) : undefined
          }
        />
        {data?.stateWasteCodes && data?.stateWasteCodes.length > 0 && (
          <WasteCodeGrid
            codes={data?.stateWasteCodes || []}
            title={titles.state}
            searchTerm={searchTerm}
            specificSearchTerm={filters.stateSearch}
            itemsPerPage={itemsPerPage}
            keyPrefix="state"
            emptyStateMessage={{
              withSearch: "No state waste codes match your search",
              withoutSearch: "No state waste codes found",
            }}
            headerAction={
              isStaff ? (
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    color: theme.palette.primary.contrastText,
                    backgroundColor: theme.palette.warning.light,
                  }}
                  onClick={() => setStateCompareOpen(true)}
                >
                  Compare
                </Button>
              ) : undefined
            }
          />
        )}
      </Grid>

      {isStaff && (
        <>
          <WasteCodeCompareDialog
            open={federalCompareOpen}
            onClose={() => setFederalCompareOpen(false)}
            wasteCodes={data?.federalWasteCodes ?? []}
          />
          <WasteCodeCompareDialog
            open={stateCompareOpen}
            onClose={() => setStateCompareOpen(false)}
            wasteCodes={data?.stateWasteCodes ?? []}
          />
        </>
      )}
    </Stack>
  );
};
